import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import DonationForm from "@/components/forms/DonationForm";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Users,
  Heart,
  Globe,
  Calendar,
  Briefcase,
  GraduationCap,
  FileText,
  Clock,
  Building
} from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function GetInvolvedPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-white py-20 px-4 sm:px-6 lg:px-8 border-b border-slate-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h1 className="text-4xl font-extrabold sm:text-5xl md:text-6xl text-slate-900">Get Involved</h1>
              <p className="mt-6 text-xl text-slate-800 max-w-3xl mx-auto">
                There are many ways to support our mission and make a difference in people's lives.
              </p>
            </div>
          </div>
        </section>

        {/* Ways to Get Involved */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <Tabs defaultValue="donate" className="w-full">
              <div className="text-center mb-12">
                <TabsList className="flex flex-wrap justify-center space-x-4 max-w-3xl mx-auto p-4 rounded-xl bg-slate-100">
                  <TabsTrigger 
                    value="donate" 
                    className="text-slate-900 data-[state=active]:text-primary-700 px-8 py-3 rounded-lg data-[state=active]:bg-white shadow data-[state=active]:shadow-md"
                  >
                    Donate
                  </TabsTrigger>
                  <TabsTrigger 
                    value="volunteer" 
                    className="text-slate-900 data-[state=active]:text-primary-700 px-8 py-3 rounded-lg data-[state=active]:bg-white shadow data-[state=active]:shadow-md"
                  >
                    Volunteer
                  </TabsTrigger>
                  <TabsTrigger 
                    value="partner" 
                    className="text-slate-900 data-[state=active]:text-primary-700 px-8 py-3 rounded-lg data-[state=active]:bg-white shadow data-[state=active]:shadow-md"
                  >
                    Partner With Us
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="donate" id="donate" className="border border-slate-200 rounded-lg p-8 bg-white shadow-sm mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                  <div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-8">Support Our Mission</h2>
                    <p className="text-lg text-slate-800 mb-6">
                      Your financial contribution helps us provide essential services to individuals working to overcome barriers to employment and stability.
                    </p>
                    <p className="text-lg text-slate-800 mb-8">
                      Every donation makes a difference in someone's life journey toward independence and success.
                    </p>
                    
                    <div className="bg-primary-50 p-6 rounded-lg mb-8">
                      <h3 className="text-xl font-semibold text-slate-900 mb-4">Your Impact</h3>
                      <ul className="space-y-4">
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <FileText className="h-4 w-4" />
                          </span>
                          <div>
                            <span className="font-medium text-slate-900">$50</span>
                            <p className="text-slate-600">Provides resume and interview coaching for one client</p>
                          </div>
                        </li>
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <Briefcase className="h-4 w-4" />
                          </span>
                          <div>
                            <span className="font-medium text-slate-900">$100</span>
                            <p className="text-slate-600">Sponsors job readiness training and certification</p>
                          </div>
                        </li>
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <Users className="h-4 w-4" />
                          </span>
                          <div>
                            <span className="font-medium text-slate-900">$250</span>
                            <p className="text-slate-600">Funds a month of personalized case management</p>
                          </div>
                        </li>
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <Building className="h-4 w-4" />
                          </span>
                          <div>
                            <span className="font-medium text-slate-900">$1,000</span>
                            <p className="text-slate-600">Supports our housing stability program for multiple clients</p>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  <div>
                    <DonationForm />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="volunteer" id="volunteer" className="border border-slate-200 rounded-lg p-8 bg-white shadow-sm mt-6">
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-8">Volunteer Opportunities</h2>
                  <p className="text-lg text-slate-800 mb-8 max-w-3xl">
                    Sharing your time and talents is one of the most valuable ways to support our mission. Our volunteers play a crucial role in helping clients build new futures.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                    <Card>
                      <CardHeader>
                        <div className="flex items-center mb-2">
                          <div className="p-2 rounded-md bg-primary-100 mr-3">
                            <Briefcase className="h-5 w-5 text-primary-600" />
                          </div>
                          <CardTitle>Job Coach</CardTitle>
                        </div>
                        <CardDescription>
                          4-6 hours/month, in-person or virtual
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-600 mb-4">
                          Help clients with resume writing, interview preparation, and job search strategies. Provide feedback on professional communication and workplace etiquette.
                        </p>
                        <p className="text-sm text-slate-500">
                          <strong>Skills needed:</strong> Professional work experience, communication skills, patience
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <div className="flex items-center mb-2">
                          <div className="p-2 rounded-md bg-primary-100 mr-3">
                            <GraduationCap className="h-5 w-5 text-primary-600" />
                          </div>
                          <CardTitle>Skills Trainer</CardTitle>
                        </div>
                        <CardDescription>
                          4-8 hours/month, in-person
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-600 mb-4">
                          Teach workshops on specific skills like computer literacy, financial management, or industry-specific competencies to groups of clients.
                        </p>
                        <p className="text-sm text-slate-500">
                          <strong>Skills needed:</strong> Expertise in specific area, teaching or presenting experience
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <div className="flex items-center mb-2">
                          <div className="p-2 rounded-md bg-primary-100 mr-3">
                            <Heart className="h-5 w-5 text-primary-600" />
                          </div>
                          <CardTitle>Mentor</CardTitle>
                        </div>
                        <CardDescription>
                          6-8 hours/month, in-person or virtual
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-600 mb-4">
                          Build a relationship with a client through regular meetings, providing guidance, support, and encouragement as they navigate their journey to stability.
                        </p>
                        <p className="text-sm text-slate-500">
                          <strong>Skills needed:</strong> Empathy, good listening skills, reliability
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <div className="flex items-center mb-2">
                          <div className="p-2 rounded-md bg-primary-100 mr-3">
                            <Calendar className="h-5 w-5 text-primary-600" />
                          </div>
                          <CardTitle>Event Support</CardTitle>
                        </div>
                        <CardDescription>
                          As needed, in-person
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-600 mb-4">
                          Help with job fairs, fundraising events, workshops, and other special events. Tasks may include setup, greeting, facilitation, and cleanup.
                        </p>
                        <p className="text-sm text-slate-500">
                          <strong>Skills needed:</strong> Dependability, adaptability, people skills
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <div className="flex items-center mb-2">
                          <div className="p-2 rounded-md bg-primary-100 mr-3">
                            <Globe className="h-5 w-5 text-primary-600" />
                          </div>
                          <CardTitle>Outreach Ambassador</CardTitle>
                        </div>
                        <CardDescription>
                          4-6 hours/month, flexible
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-600 mb-4">
                          Help raise awareness about StaffAbility by representing us at community events, speaking to potential employer partners, and sharing our mission.
                        </p>
                        <p className="text-sm text-slate-500">
                          <strong>Skills needed:</strong> Communication skills, enthusiasm, networking abilities
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <div className="flex items-center mb-2">
                          <div className="p-2 rounded-md bg-primary-100 mr-3">
                            <Clock className="h-5 w-5 text-primary-600" />
                          </div>
                          <CardTitle>Administrative Support</CardTitle>
                        </div>
                        <CardDescription>
                          Flexible hours, in-person or virtual
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-600 mb-4">
                          Assist with office tasks, data entry, phone calls, filing, and other administrative duties that help our programs run smoothly.
                        </p>
                        <p className="text-sm text-slate-500">
                          <strong>Skills needed:</strong> Organization, attention to detail, basic computer skills
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="bg-primary-50 p-8 rounded-lg max-w-3xl mx-auto text-center">
                    <h3 className="text-xl font-semibold text-slate-900 mb-4">Ready to Volunteer?</h3>
                    <p className="text-slate-600 mb-6">
                      Complete our volunteer application to share your interests and availability. We'll follow up to discuss the best way for you to get involved.
                    </p>
                    <Link href="/contact">
                      <Button size="lg">Volunteer Application</Button>
                    </Link>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="partner" id="partner" className="border border-slate-200 rounded-lg p-8 bg-white shadow-sm mt-6">
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-8">Partnership Opportunities</h2>
                  <p className="text-lg text-slate-800 mb-8 max-w-3xl">
                    Partnerships are essential to our holistic approach. We collaborate with businesses, nonprofits, and community organizations to create comprehensive support systems.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                    <div className="bg-white p-6 rounded-lg shadow-md border border-slate-100">
                      <h3 className="text-xl font-semibold text-slate-900 mb-4">For Businesses</h3>
                      <ul className="space-y-4">
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <Briefcase className="h-4 w-4" />
                          </span>
                          <div>
                            <p className="font-medium text-slate-900">Employment Partners</p>
                            <p className="text-slate-600">Commit to hiring qualified candidates from our program and providing supportive work environments.</p>
                          </div>
                        </li>
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <GraduationCap className="h-4 w-4" />
                          </span>
                          <div>
                            <p className="font-medium text-slate-900">Training Partners</p>
                            <p className="text-slate-600">Provide industry-specific training, internships, or apprenticeships to help clients develop skills.</p>
                          </div>
                        </li>
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <Users className="h-4 w-4" />
                          </span>
                          <div>
                            <p className="font-medium text-slate-900">Corporate Volunteers</p>
                            <p className="text-slate-600">Engage your team in volunteer opportunities that make a difference while building team cohesion.</p>
                          </div>
                        </li>
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <Heart className="h-4 w-4" />
                          </span>
                          <div>
                            <p className="font-medium text-slate-900">Corporate Sponsors</p>
                            <p className="text-slate-600">Support specific programs, events, or initiatives through financial contributions or in-kind donations.</p>
                          </div>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg shadow-md border border-slate-100">
                      <h3 className="text-xl font-semibold text-slate-900 mb-4">For Nonprofits & Community Organizations</h3>
                      <ul className="space-y-4">
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <Building className="h-4 w-4" />
                          </span>
                          <div>
                            <p className="font-medium text-slate-900">Housing Partners</p>
                            <p className="text-slate-600">Help provide emergency, transitional, or permanent housing options for clients experiencing housing instability.</p>
                          </div>
                        </li>
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <Heart className="h-4 w-4" />
                          </span>
                          <div>
                            <p className="font-medium text-slate-900">Recovery Support</p>
                            <p className="text-slate-600">Offer addiction recovery or mental health services that complement our employment programs.</p>
                          </div>
                        </li>
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <FileText className="h-4 w-4" />
                          </span>
                          <div>
                            <p className="font-medium text-slate-900">Legal Services</p>
                            <p className="text-slate-600">Provide legal assistance for record expungement, child support issues, or other legal barriers.</p>
                          </div>
                        </li>
                        <li className="flex">
                          <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 mr-3">
                            <Globe className="h-4 w-4" />
                          </span>
                          <div>
                            <p className="font-medium text-slate-900">Cross-Referral Partners</p>
                            <p className="text-slate-600">Establish formalized referral systems to ensure clients receive comprehensive support across organizations.</p>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="bg-primary-50 p-8 rounded-lg max-w-3xl mx-auto text-center">
                    <h3 className="text-xl font-semibold text-slate-900 mb-4">Interested in Partnering?</h3>
                    <p className="text-slate-600 mb-6">
                      Contact us to discuss how we can work together to create more opportunities and support for individuals overcoming barriers.
                    </p>
                    <Link href="/contact">
                      <Button size="lg">Contact Us About Partnerships</Button>
                    </Link>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* Impact Stories */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Your Support Makes a Difference</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                See how donors, volunteers, and partners have helped transform lives.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg overflow-hidden shadow-md">
                <div className="h-48 bg-slate-200">
                  <img 
                    className="w-full h-full object-cover" 
                    src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" 
                    alt="Community job fair"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900">Annual Employer Breakfast</h3>
                  <p className="text-primary-600 text-sm mb-3">Sponsored by Metro Business Alliance</p>
                  <p className="text-slate-600 mb-4">
                    "The annual breakfast our company sponsored connected us with five incredible employees who have been with us for over two years now. It was a win-win for everyone involved."
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg overflow-hidden shadow-md">
                <div className="h-48 bg-slate-200">
                  <img 
                    className="w-full h-full object-cover" 
                    src="https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" 
                    alt="Volunteer teaching computer skills"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900">Digital Literacy Program</h3>
                  <p className="text-primary-600 text-sm mb-3">Volunteer-Led Initiative</p>
                  <p className="text-slate-600 mb-4">
                    "As a volunteer instructor, I've seen clients go from never having used a computer to confidently applying for jobs online. The transformation is incredible."
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg overflow-hidden shadow-md">
                <div className="h-48 bg-slate-200">
                  <img 
                    className="w-full h-full object-cover" 
                    src="https://images.unsplash.com/photo-1593113646773-028c64a8f1b8?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" 
                    alt="Housing program apartment keys"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900">Housing First Partnership</h3>
                  <p className="text-primary-600 text-sm mb-3">Collaboration with Urban Housing Initiative</p>
                  <p className="text-slate-600 mb-4">
                    "Our partnership has helped 32 clients secure stable housing this year, creating the foundation they needed to focus on their employment goals."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-primary-700 text-white">
          <div className="max-w-7xl mx-auto">
            <div className="md:flex md:items-center md:justify-between">
              <div>
                <h2 className="text-3xl font-extrabold sm:text-4xl">Ready to make an impact?</h2>
                <p className="mt-3 text-xl text-primary-200">
                  Join our community of supporters who are changing lives every day.
                </p>
              </div>
              <div className="mt-8 flex flex-col md:flex-row md:mt-0 md:space-x-3 space-y-3 md:space-y-0">
                <Link href="#donate">
                  <Button variant="secondary" size="lg" className="w-full md:w-auto">Donate Today</Button>
                </Link>
                <Link href="#volunteer">
                  <Button variant="outline" size="lg" className="w-full md:w-auto bg-transparent border-white text-white hover:bg-primary-600">Volunteer</Button>
                </Link>
                <Link href="#partner">
                  <Button variant="outline" size="lg" className="w-full md:w-auto bg-transparent border-white text-white hover:bg-primary-600">Partner With Us</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
